<script setup>
import { ref, computed } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'
import _ from 'lodash'

import AudienceService from '@/services/AudienceService'
import { useAudienceConsole, FEATURES } from '@/components/audience/audienceConsole'

import Alert from '@/components/Atoms/Alert.vue'
import FormButton from '@/components/Atoms/FormButton.vue'
import FormInput from '@/components/Atoms/FormInput.vue'
import FormSwitch from '@/components/Atoms/FormSwitch.vue'
import ModalBox from '@/components/modals/ModalBox.vue'
import AudienceThemePicker from '@/components/audience/AudienceThemePicker.vue'

// What the viewer's panel is made of: the feature set, who gets in, how fast
// they can talk, and what it all looks like. Settings someone finishes with
// before going live — the mid-broadcast controls live on the Live room tab.

const { notify } = useNotification()

const props = defineProps({
	stream: {
		type: Object,
		required: true,
		validator(v) {
			return Object.prototype.hasOwnProperty.call(v, '_id')
		}
	},
	streamAlive: {
		type: Boolean
	}
})

const {
	settings,
	isSaving,
	persist,
	onToggle,
	usingCastr,
	isLive,
	theme,
	embedThemeColor,
	onThemeUpdate
} = useAudienceConsole()

const enabledFeatures = computed(() => FEATURES.filter((f) => settings.value[f.key]))

const hasPaywall = computed(() => !!props.stream.paywallPopupAuth)

const identityModalVisible = ref(false)

/**
 * Turning this off is the one setting on the tab whose only symptom is the
 * panel silently not existing: the token endpoint refuses to mint for a viewer
 * with no paywall-signed identity, and the player fails open by hiding itself.
 * Nothing on screen would say so, so the switch springs back and asks first.
 */
const onAnonymousToggle = async () => {
	// v-model has already flipped it. Switching it back on is safe.
	if (settings.value.anonymousAllowed) {
		await onToggle('anonymousAllowed')
		return
	}

	settings.value.anonymousAllowed = true
	identityModalVisible.value = true
}

const confirmIdentityRequired = async () => {
	identityModalVisible.value = false
	settings.value.anonymousAllowed = false

	const ok = await persist({ anonymousAllowed: false }, 'anonymousAllowed')
	if (!ok) settings.value.anonymousAllowed = true
}

const allowAnonymousAgain = async () => {
	settings.value.anonymousAllowed = true

	const ok = await persist({ anonymousAllowed: true }, 'anonymousAllowed')
	if (!ok) settings.value.anonymousAllowed = false
}

// Text drafts and blur/submit-driven saves would fight a bare v-model on
// settings; a separate input plus explicit save keeps typing "12" from
// persisting "1". Read once at setup — the shell has loaded settings before
// this pane can mount.
const slowModeInput = ref(String(settings.value.slowModeSec || 0))

const saveSlowMode = async () => {
	const seconds = _.clamp(parseInt(slowModeInput.value, 10) || 0, 0, 3600)
	slowModeInput.value = String(seconds)

	const ok = await persist({ slowModeSec: seconds }, 'slowModeSec')
	if (!ok) return

	// Tokens are minted per page render, so a live room only picks the new value
	// up through its own config. Push it there too when we are connected.
	if (isLive.value) {
		try {
			await AudienceService.updateRoomConfig(props.stream._id, { slowModeSec: seconds })
		} catch (err) {
			notify({ group: 'error', text: err.message || 'Saved, but the live room kept the old slow mode' })
			return
		}
	}

	notify({ group: 'success', text: 'Slow mode updated' })
}
</script>

<template>
	<section v-if="!usingCastr" class="bg-surface-1 rounded-xl p-4 md:p-5">
		<p class="text-sm text-surface-text-1 mb-1">Panel setup is not available.</p>
		<p class="text-tiny text-surface-7 mb-0">
			Chat is switched off. Turn it on with the switch above to configure the panel's
			features and appearance.
		</p>
	</section>

	<section v-else class="bg-surface-1 rounded-xl p-4 md:p-5">
		<h4 class="text-base mb-3">Features</h4>
		<div class="divide-y divide-surface-2">
			<div v-for="feature in FEATURES" :key="feature.key"
				class="flex items-center justify-between gap-x-4 py-3">
				<div>
					<p class="text-sm text-surface-text-1 mb-0.5">{{ feature.label }}</p>
					<p class="text-tiny text-surface-7 mb-0">{{ feature.hint }}</p>
				</div>
				<FormSwitch v-model="settings[feature.key]" :disabled="isSaving(feature.key)"
					@change="onToggle(feature.key)" />
			</div>

			<div class="flex items-center justify-between gap-x-4 py-3">
				<div>
					<p class="text-sm text-surface-text-1 mb-0.5">Country flags in chat</p>
					<p class="text-tiny text-surface-7 mb-0">
						Show a small flag next to each name, based on the viewer's country.
					</p>
				</div>
				<FormSwitch v-model="settings.countryFlags" :disabled="isSaving('countryFlags')"
					@change="onToggle('countryFlags')" />
			</div>

			<div class="flex items-center justify-between gap-x-4 py-3">
				<div>
					<p class="text-sm text-surface-text-1 mb-0.5">Open chat on player load</p>
					<p class="text-tiny text-surface-7 mb-0">
						Automatically display the chat panel when viewers load the player.
					</p>
				</div>
				<FormSwitch v-model="settings.openOnLoad" :disabled="isSaving('openOnLoad')"
					@change="onToggle('openOnLoad')" />
			</div>

			<div class="py-3">
				<div class="flex items-center justify-between gap-x-4">
					<div>
						<p class="text-sm text-surface-text-1 mb-0.5">Allow anonymous viewers</p>
						<p class="text-tiny text-surface-7 mb-0">
							Off means the panel only appears for viewers carrying a paywall-signed
							identity. On a stream without a paywall, that is nobody.
						</p>
					</div>
					<FormSwitch v-model="settings.anonymousAllowed" :disabled="isSaving('anonymousAllowed')"
						@change="onAnonymousToggle" />
				</div>

				<!-- The setting announces itself, because its only other
				symptom is the panel not being there. -->
				<Alert v-if="!settings.anonymousAllowed" :type="hasPaywall ? 'warning' : 'error'"
					class="mt-3">
					<span class="flex flex-wrap items-center gap-x-2 gap-y-1">
						<template v-if="hasPaywall">
							Anonymous viewers are off. Only viewers who arrive through the paywall
							with a signed identity see the panel; anyone reaching the player another
							way gets nothing.
						</template>
						<template v-else>
							Anonymous viewers are off and this stream has no paywall, so the panel is
							hidden for everyone watching.
						</template>
						<button type="button" class="underline font-medium" :disabled="isSaving('anonymousAllowed')"
							@click="allowAnonymousAgain">
							Allow anonymous viewers
						</button>
					</span>
				</Alert>
			</div>

			<div class="flex flex-wrap items-center justify-between gap-x-4 gap-y-2 py-3">
				<div>
					<p class="text-sm text-surface-text-1 mb-0.5">Slow mode</p>
					<p class="text-tiny text-surface-7 mb-0">
						Seconds a viewer must wait between messages. 0 turns it off.
						Saving also applies immediately to a room that is already live.
					</p>
				</div>
				<div class="flex items-center gap-x-2">
					<FormInput v-model="slowModeInput" type="number" custom-classes="w-24" />
					<FormButton type="secondary" size="sm" :is-loading="isSaving('slowModeSec')"
						@click="saveSlowMode">
						Save
					</FormButton>
				</div>
			</div>
		</div>

		<Alert v-if="!enabledFeatures.length" type="warning" class="mt-4"
			text="Every feature is switched off, so the panel will not appear in the player." />

		<div class="border-t border-surface-3 mt-4 pt-4">
			<h4 class="text-base mb-1">Appearance</h4>
			<p class="text-sm text-surface-8 mb-3">
				Colours for the audience panel in your player. Viewers see the change on their next
				player load.
			</p>
			<AudienceThemePicker :theme="theme" :embed-theme-color="embedThemeColor"
				:disabled="isSaving('theme')" @update="onThemeUpdate" />
		</div>

		<ModalBox v-model="identityModalVisible" title="Turn off anonymous viewers?" size="sm" scroll-body>
			<div class="px-4 pb-4 text-sm text-surface-9 sm:px-6 sm:pb-6">
				<p class="text-surface-text-1 mb-3">
					With this off, the panel only loads for viewers who arrive carrying a paywall-signed
					identity. Everyone else is refused a token, and the player responds by hiding the panel
					entirely — no chat, no Q&amp;A, no polls, and no message saying why.
				</p>
				<p v-if="hasPaywall" class="mb-3">
					This stream is behind a paywall, so viewers coming through it will be identified. Anyone
					reaching the player another way — a direct embed, or a link that skips the paywall — will
					see no panel at all.
				</p>
				<p v-else class="mb-3">
					This stream is not behind a paywall, so no viewer will have that identity. The panel will
					disappear for everyone watching. Only turn this off if you are about to put the stream
					behind a paywall.
				</p>
				<p class="mb-0">
					You can switch it back on at any time; viewers get the panel again on their next player
					load.
				</p>
			</div>
			<template #footer>
				<footer class="flex flex-col gap-2 border-t border-surface-3 p-4 sm:flex-row-reverse sm:p-6">
					<FormButton class="w-full justify-center sm:w-auto" :type="hasPaywall ? 'main' : 'red'"
						:is-loading="isSaving('anonymousAllowed')" @click="confirmIdentityRequired">
						{{ hasPaywall ? 'Turn it off' : 'Turn it off anyway' }}
					</FormButton>
					<FormButton class="w-full justify-center sm:w-auto" type="secondary"
						@click="identityModalVisible = false">
						Cancel
					</FormButton>
				</footer>
			</template>
		</ModalBox>
	</section>
</template>
